import { useAuth } from '@/context/AuthContext';
import useDowntimeFlag from '@/hooks/useDowntimeFlag';
import useLimits from '@/hooks/useLimits';
import { useSettings } from '@/hooks/useSettings';
import { useTeamInputText } from '@/hooks/useTeamInputText';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { cn } from '@/utils/utils';
import { ArrowUp } from 'lucide-react';
import { motion, useAnimationControls } from 'motion/react';
import { nanoid } from 'nanoid';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { z } from 'zod';
import ChatUnavailableBanner from '../Chat/ChatUnavailableBanner';
import { useCreateChat } from '../ChatV2/ChatV2Input/hooks/useCreateChat';
import { useUpgrade } from '../Modals/UpgradeModal';
import ContextDropdown, { useSourceContext } from '../Settings/ContextDropdown';
import MaxLengthDropdown from '../Settings/MaxLengthDropdown';
import ModelDropdown from '../Settings/ModelDropdown';
import { Form, useForm } from '../ui/Form';
import { Icon } from '../ui/Icon';

interface AppLandingFormProps {
  onInputChange?: () => void;
  onBlur?: () => void;
  disabledInput?: boolean;
}

const AppLandingForm = ({
  onInputChange,
  onBlur,
  disabledInput,
}: AppLandingFormProps) => {
  const form = useForm({
    schema: z.object({
      input: z.string().min(1),
    }),
  });

  const { track } = useAnalytics();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { settings } = useSettings();
  const controls = useAnimationControls();
  const { isDowntime } = useDowntimeFlag();
  const { inputText, setInputText } = useTeamInputText();

  const [isLoading, setIsLoading] = useState(false);

  const [isFileInputOpen, setIsFileInputOpen] = useState(false);
  const { context } = useSourceContext();

  const { createChat } = useCreateChat();

  const { showUpgrade } = useUpgrade();
  const { exceededLimits } = useLimits();
  const { user } = useAuth();

  const handleSubmit = async ({ input }: { input: string }) => {
    if (
      exceededLimits?.isWordLimitExceeded &&
      !user?.teamsMemberOf.some((teamMember) => teamMember.status === 'ACTIVE')
    ) {
      showUpgrade('messageLimit');
      track('wordCountLimitHit', {
        userId: user?.id,
      });
      track('Paywall Shown', {
        reason: 'Word Count Limit Hit',
        type: 'Message',
      });

      return;
    }

    setIsLoading(true);

    if (isDowntime) {
      controls.start({
        y: [5, 0],
        x: [-5, 0],
      });
      return form.reset();
    }

    const { id } = await createChat({
      id: nanoid(),
      includedSources: [
        context === 'library'
          ? { type: 'library' }
          : { type: 'generalKnowledge' },
      ],
    });

    router.push(
      `/b/${id}?model=${settings.modelName}&maxLength=${settings.maxLength}&query=${encodeURIComponent(input)}`,
    );

    track('chat_nothing_created', {
      chatbotId: id,
      query: input,
      model: settings.modelName,
      maxLength: settings.maxLength,
      context: context,
    });
  };

  form.watch((values) => {
    if (values.input && onInputChange) onInputChange();
  });

  const disableSubmit = form.formState.isSubmitting || disabledInput;

  const disabledButton = !form.formState.isValid || isLoading;

  useEffect(() => {
    if (inputText) {
      form.setValue('input', inputText);
      form.trigger('input');
      setInputText(''); // Clear after using
    }
  }, [inputText, form]);

  // Add effect to handle file dialog close
  useEffect(() => {
    const handleFocus = () => {
      // When window regains focus, file dialog must have been closed
      setIsFileInputOpen(false);
    };

    if (isFileInputOpen) {
      window.addEventListener('focus', handleFocus);
      return () => window.removeEventListener('focus', handleFocus);
    }
  }, [isFileInputOpen]);

  return (
    <>
      <motion.div
        animate={controls}
        className="flex z-0 flex-col justify-between gap-1 rounded-[15px] bg-popover-hover-solid p-[2px] border border-border"
      >
        {isDowntime && <ChatUnavailableBanner />}

        <div
          onClick={() => {
            form.setFocus('input');
          }}
          className="relative flex flex-col justify-between shadow-feint rounded-lg bg-popover max-h-[24rem] min-h-[104px] pr-2 pl-3 pt-2 pb-2 z-10"
        >
          <Form {...form}>
            <form id="prompt" onSubmit={form.handleSubmit(handleSubmit)}>
              <TextareaAutosize
                autoFocus
                maxRows={10}
                className="w-full !h-11\ leading-[22px] text-sm max-sm:text-[16px] resize-none bg-transparent disabled:text-textGray2 focus:outline-none placeholder:text-textGray2 scrollbar-hide"
                placeholder={
                  context === 'library'
                    ? 'Ask your library a question...'
                    : context === 'generalKnowledge'
                      ? 'Ask a general knowledge question...'
                      : 'Ask Anara a question...'
                }
                disabled={disableSubmit}
                onKeyDown={(e) => {
                  if (e.keyCode === 13 && e.shiftKey === false) {
                    e.preventDefault();

                    if (disableSubmit) return;

                    form.handleSubmit(handleSubmit)();
                  }
                }}
                {...form.register('input')}
                onBlur={onBlur}
              />
            </form>
          </Form>

          <div className="flex flex-row items-center justify-between w-full">
            <div className="flex flex-row gap-2 mt-auto">
              <ModelDropdown mode="chat" />
              <MaxLengthDropdown />
              <ContextDropdown />
            </div>

            <div className="flex flex-row gap-1 right-6">
              <input
                type="file"
                ref={fileInputRef}
                disabled={isLoading}
                onChange={() => {
                  // handleFileChangeFn(e);
                  setIsFileInputOpen(false);
                }}
                onClick={() => setIsFileInputOpen(true)}
                className="hidden"
                multiple
                accept=".png,.jpg,.jpeg,.webp,.gif"
              />

              <button
                disabled={disabledButton}
                form="prompt"
                className={cn(
                  'flex justify-center items-center w-7 h-7 rounded-md ease-in transition-all duration-150 cursor-pointer bg-foreground',
                  disabledButton && 'bg-textGray4 dark:bg-muted-foreground',
                )}
              >
                <Icon
                  as={ArrowUp}
                  className="text-popover h-4 w-4"
                  strokeWidth={3}
                />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default AppLandingForm;
